import { importDataDescriptorsFromJson } from '../ngsild/data-descriptor.logic';
import type {
  NgsildBasicInfo,
  NgsildMetadataInfo,
  NgsildOnboardingPayload,
  NgsildYearRange,
} from '../ngsild/ngsild.models';
import {
  additionalInfoMutualExclusionError,
  emptyTemplateAdditionalInfo,
  type AdditionalInfoFaqRow,
} from '../../shared/additional-info/additional-info.logic';

/** Catalogue create-item type for datasets (MahaAgX / NGSI-LD onboarding). */
export const DATASET_METADATA_TYPE = 'adex:DataBank' as const;

/** Ignored on upload — documents allowed values for template users. */
export interface DatasetMetadataFieldGuide {
  _note: string;
  title: string;
  permission: { allowed: readonly string[]; description: string };
  shortDescription: string;
  longDescription: string;
  license: {
    allowed: readonly string[];
    description: string;
    otherLicense: string;
  };
  uploadFrequency: { allowed: readonly string[]; description: string };
  yearRange: {
    description: string;
    startYear: string;
    endYear: string;
  };
  geoCoverage: string;
  datasetType: {
    allowed: readonly string[];
    description: string;
    fileFormatByDatasetType: Record<string, readonly string[]>;
  };
  fileFormat: {
    description: string;
    note: string;
  };
  tags: string;
  dataDescriptors: {
    description: string;
    parameterType: { allowed: readonly string[] };
    schemaType: { allowed: readonly string[] };
    example: Record<string, string>;
  };
  additionalInfo: {
    _note: string;
    faq: string;
    information: string;
  };
}

/** Shape users fill in and upload; maps to {@link NgsildOnboardingPayload} + create-item API. */
export interface DatasetMetadataTemplateJson {
  _fieldGuide: DatasetMetadataFieldGuide;
  type: string[];
  title: string;
  permission: 'Open' | 'Restricted' | 'Private';
  shortDescription: string;
  longDescription: string;
  license: string;
  otherLicense: string;
  uploadFrequency: string;
  yearRange: { startYear: number; endYear: number };
  geoCoverage: string;
  datasetType: string;
  fileFormat: string;
  tags: string[];
  dataDescriptors: Array<{
    attributeName: string;
    parameterType: string;
    schemaType: string;
    description?: string;
    unitCode?: string;
    unitText?: string;
  }>;
  additionalInfo: {
    faq: Array<{ question: string; answer: string }>;
    information: string;
  };
}

const PERMISSION_VALUES = ['Open', 'Restricted', 'Private'] as const;
const LICENSE_VALUES = [
  'Open Government License, India',
  'CC-BY 4.0',
  'Other',
] as const;
const UPLOAD_FREQUENCY_VALUES = [
  'OneTime',
  'daily',
  'weekly',
  'monthly',
  'Quarterly',
  'semi-annually',
  'Annually',
] as const;
const DATASET_TYPE_VALUES = [
  'Structured',
  'semi Structured',
  'unStructured',
] as const;
const FILE_FORMAT_BY_DATASET_TYPE: Record<string, readonly string[]> = {
  Structured: ['CSV', 'JSON', 'XML', 'XLSX', 'Parquet', 'Avro'],
  'semi Structured': ['JSON', 'XML', 'YAML', 'JSONL'],
  unStructured: ['PDF', 'DOCX', 'TXT', 'PNG', 'JPG', 'MP4', 'MP3'],
};

/** Fixed dataset type and file format required for OGC Feature datasets. */
export const OGC_FEATURE_DATASET_TYPE = 'semi Structured' as const;
export const OGC_FEATURE_FILE_FORMAT = 'gpkg' as const;

/** Map API/template casing (e.g. csv) to the label used in form dropdowns (e.g. CSV). */
export function matchFileFormatToOptionValue(
  fileFormat: string,
  allowedLabels: readonly string[],
): string {
  const trimmed = String(fileFormat ?? '').trim();
  if (!trimmed) {
    return '';
  }
  const match = allowedLabels.find(
    (label) =>
      label === trimmed ||
      label.toLowerCase() === trimmed.toLowerCase(),
  );
  return match ?? trimmed;
}
const DESCRIPTOR_PARAMETER_TYPES = ['ValueDescriptor', 'Relationship'] as const;
const DESCRIPTOR_SCHEMA_TYPES = [
  'Text',
  'Number',
  'datetime',
  'Point',
  'Linestring',
  'place',
  'TimeSeriesAggregation',
] as const;

function buildFieldGuide(): DatasetMetadataFieldGuide {
  return {
    _note:
      'This section is for your reference only. You may keep or delete it before upload — it is not sent to the API.',
    title: 'Dataset display name (required). Max 200 characters.',
    permission: {
      allowed: PERMISSION_VALUES,
      description:
        'Open = public catalogue access. Restricted = private / access-controlled.',
    },
    shortDescription: 'Brief summary (max 200 characters).',
    longDescription: 'Full description (max 2000 characters).',
    license: {
      allowed: LICENSE_VALUES,
      description: 'Pick one value exactly as listed.',
      otherLicense:
        'Leave empty unless license is "Other". When license is "Other", enter your custom license text here (required).',
    },
    uploadFrequency: {
      allowed: UPLOAD_FREQUENCY_VALUES,
      description: 'How often the dataset is updated on the platform.',
    },
    yearRange: {
      description:
        'Inclusive calendar years that the dataset content covers (not upload date).',
      startYear: 'Four-digit year (YYYY) — earliest year in the range, e.g. 2020',
      endYear:
        'Four-digit year (YYYY) — latest year in the range; must be greater than or equal to startYear, e.g. 2024',
    },
    geoCoverage:
      'Geographic scope in plain text (e.g. "India", "Rajasthan", "Global").',
    datasetType: {
      allowed: DATASET_TYPE_VALUES,
      description:
        'Use the exact spelling shown (including spaces in "semi Structured").',
      fileFormatByDatasetType: FILE_FORMAT_BY_DATASET_TYPE,
    },
    fileFormat: {
      description:
        'Must match one of the formats listed for your datasetType in fileFormatByDatasetType above.',
      note: 'Stored in lowercase in the API (e.g. csv, json). Use the label spelling from the list.',
    },
    tags: 'Array of at least one keyword string (max 20 characters each), e.g. ["agriculture", "forest"].',
    dataDescriptors: {
      description:
        'Schema of attributes in the dataset. At least one entry recommended.',
      parameterType: {
        allowed: DESCRIPTOR_PARAMETER_TYPES,
      },
      schemaType: {
        allowed: DESCRIPTOR_SCHEMA_TYPES,
      },
      example: {
        attributeName: 'Name of the column or attribute',
        parameterType: 'ValueDescriptor',
        schemaType: 'Text',
        description: 'Human-readable description of the attribute',
      },
    },
    additionalInfo: {
      _note:
        'Optional. Fill faq or information, not both. Leave both empty to skip. Not sent as catalogue metadata — posted as provider feedback after create.',
      faq: 'Array of {question, answer}. Answer 30-200 characters when filled. Leave empty if using information.',
      information:
        'Free text, max 1000 characters. Leave empty if using faq.',
    },
  };
}

/** Remove template-only keys (e.g. _fieldGuide) before validation or API mapping. */
export function stripTemplateGuideKeys(
  json: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(json)) {
    if (key.startsWith('_')) continue;
    out[key] = value;
  }
  return out;
}

export function buildDatasetMetadataTemplate(): DatasetMetadataTemplateJson {
  return {
    _fieldGuide: buildFieldGuide(),
    type: [DATASET_METADATA_TYPE],
    title: '',
    permission: 'Open',
    shortDescription:
      'Brief description of the dataset (max 200 characters).',
    longDescription:
      'Detailed description of the dataset (max 2000 characters). Include purpose, content, and methodology.',
    license: 'Open Government License, India',
    otherLicense: '',
    uploadFrequency: 'OneTime',
    yearRange: {
      startYear: 2020,
      endYear: 2024,
    },
    geoCoverage: 'India',
    datasetType: 'Structured',
    fileFormat: 'csv',
    tags: ['example-tag'],
    dataDescriptors: [
      {
        attributeName: 'cropType',
        parameterType: 'ValueDescriptor',
        schemaType: 'Text',
        description: 'Type of crop grown in the field',
      },
    ],
    additionalInfo: emptyTemplateAdditionalInfo(),
  };
}

function buildFieldGuideOgcFeature(): DatasetMetadataFieldGuide {
  const base = buildFieldGuide();
  return {
    ...base,
    datasetType: {
      ...base.datasetType,
      description:
        'For OGC Feature datasets this MUST be exactly "semi Structured". Do not change this value.',
      fileFormatByDatasetType: {
        'semi Structured': [OGC_FEATURE_FILE_FORMAT],
      },
    },
    fileFormat: {
      description:
        'For OGC Feature datasets this MUST be exactly "gpkg". Do not change this value.',
      note: 'Stored in lowercase in the API. Only "gpkg" (GeoPackage) is accepted for OGC Feature datasets.',
    },
  };
}

/** Template pre-filled with the values required for OGC Feature dataset onboarding. */
export function buildDatasetMetadataTemplateOgcFeature(): DatasetMetadataTemplateJson {
  const base = buildDatasetMetadataTemplate();
  return {
    ...base,
    _fieldGuide: buildFieldGuideOgcFeature(),
    datasetType: OGC_FEATURE_DATASET_TYPE,
    fileFormat: OGC_FEATURE_FILE_FORMAT,
  };
}

/** CSV template pre-filled for OGC Feature dataset onboarding. */
export function buildDatasetMetadataTemplateCsvOgcFeature(): string {
  const t = buildDatasetMetadataTemplateOgcFeature();
  const lines: string[] = [
    '# Dataset metadata template (OGC Feature) — datasetType and fileFormat are fixed; do not change them.',
    rowToCsvLine(['field', 'value', 'notes']),
    rowToCsvLine(['type', DATASET_METADATA_TYPE, `Must be ${DATASET_METADATA_TYPE}`]),
    rowToCsvLine(['title', t.title, 'Dataset display name (required). Max 200 characters.']),
    rowToCsvLine(['permission', t.permission, PERMISSION_VALUES.join(' | ')]),
    rowToCsvLine(['shortDescription', t.shortDescription, 'Max 200 characters.']),
    rowToCsvLine(['longDescription', t.longDescription, 'Max 2000 characters.']),
    rowToCsvLine(['license', t.license, LICENSE_VALUES.join(' | ')]),
    rowToCsvLine(['otherLicense', t.otherLicense, 'Required only when license is Other.']),
    rowToCsvLine(['uploadFrequency', t.uploadFrequency, UPLOAD_FREQUENCY_VALUES.join(' | ')]),
    rowToCsvLine(['startYear', t.yearRange.startYear, 'YYYY — earliest year in range']),
    rowToCsvLine(['endYear', t.yearRange.endYear, 'YYYY — latest year in range']),
    rowToCsvLine(['geoCoverage', t.geoCoverage, 'e.g. India, Rajasthan, Global']),
    rowToCsvLine([
      'datasetType',
      t.datasetType,
      'REQUIRED for OGC Feature: must be exactly "semi Structured"',
    ]),
    rowToCsvLine([
      'fileFormat',
      t.fileFormat,
      'REQUIRED for OGC Feature: must be exactly "gpkg"',
    ]),
    rowToCsvLine(['tags', t.tags.join(';'), 'Separate multiple tags with semicolons (;)']),
    rowToCsvLine([
      'information',
      '',
      'Optional additional info. Max 1000 characters. Leave empty if using FAQ below. Do not fill both FAQ and information.',
    ]),
    '',
    '# Data descriptors — one attribute per row',
    rowToCsvLine(['attributeName', 'parameterType', 'schemaType', 'description']),
  ];

  for (const d of t.dataDescriptors) {
    lines.push(rowToCsvLine([d.attributeName, d.parameterType, d.schemaType, d.description ?? '']));
  }

  lines.push(...additionalInfoFaqCsvLines());

  return lines.join('\r\n');
}

export function downloadDatasetMetadataTemplateOgcFeature(
  filename = 'dataset-metadata-template-ogc-feature.json',
): void {
  const json = JSON.stringify(buildDatasetMetadataTemplateOgcFeature(), null, 2);
  downloadTextFile(json, filename, 'application/json');
}

export function downloadDatasetMetadataTemplateCsvOgcFeature(
  filename = 'dataset-metadata-template-ogc-feature.csv',
): void {
  downloadTextFile(
    buildDatasetMetadataTemplateCsvOgcFeature(),
    filename,
    'text/csv;charset=utf-8',
  );
}

function escapeCsvCell(value: string | number): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowToCsvLine(cells: Array<string | number>): string {
  return cells.map(escapeCsvCell).join(',');
}

function additionalInfoFaqCsvLines(): string[] {
  return [
    '',
    '# Additional information FAQ (optional) — one question per row; leave empty if using information above',
    rowToCsvLine(['question', 'answer']),
    rowToCsvLine(['', '']),
  ];
}

function additionalInfoFromCsv(
  information: string,
  faqRows: AdditionalInfoFaqRow[],
): { faq: AdditionalInfoFaqRow[]; information: string } {
  return { faq: faqRows, information };
}

/** Parse CSV text into rows (handles quoted fields). */
export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ',') {
      row.push(cell);
      cell = '';
      continue;
    }
    if (ch === '\n' || (ch === '\r' && next === '\n')) {
      if (ch === '\r') i++;
      row.push(cell);
      cell = '';
      if (row.some((c) => c.trim() !== '')) {
        rows.push(row);
      }
      row = [];
      continue;
    }
    if (ch === '\r') {
      row.push(cell);
      cell = '';
      if (row.some((c) => c.trim() !== '')) {
        rows.push(row);
      }
      row = [];
      continue;
    }
    cell += ch;
  }

  row.push(cell);
  if (row.some((c) => c.trim() !== '')) {
    rows.push(row);
  }
  return rows;
}

/** Build CSV template: one field per row (field | value | notes), then descriptors. */
export function buildDatasetMetadataTemplateCsv(): string {
  const t = buildDatasetMetadataTemplate();
  const lines: string[] = [
    '# Dataset metadata template — fill the value column (one field per row).',
    rowToCsvLine(['field', 'value', 'notes']),
    rowToCsvLine(['type', DATASET_METADATA_TYPE, `Must be ${DATASET_METADATA_TYPE}`]),
    rowToCsvLine(['title', t.title, 'Dataset display name (required). Max 200 characters.']),
    rowToCsvLine([
      'permission',
      t.permission,
      PERMISSION_VALUES.join(' | '),
    ]),
    rowToCsvLine(['shortDescription', t.shortDescription, 'Max 200 characters.']),
    rowToCsvLine(['longDescription', t.longDescription, 'Max 2000 characters.']),
    rowToCsvLine(['license', t.license, LICENSE_VALUES.join(' | ')]),
    rowToCsvLine([
      'otherLicense',
      t.otherLicense,
      'Required only when license is Other.',
    ]),
    rowToCsvLine([
      'uploadFrequency',
      t.uploadFrequency,
      UPLOAD_FREQUENCY_VALUES.join(' | '),
    ]),
    rowToCsvLine(['startYear', t.yearRange.startYear, 'YYYY — earliest year in range']),
    rowToCsvLine(['endYear', t.yearRange.endYear, 'YYYY — latest year in range']),
    rowToCsvLine(['geoCoverage', t.geoCoverage, 'e.g. India, Rajasthan, Global']),
    rowToCsvLine(['datasetType', t.datasetType, DATASET_TYPE_VALUES.join(' | ')]),
    rowToCsvLine([
      'fileFormat',
      t.fileFormat,
      'Lowercase in API (e.g. csv). Must match datasetType.',
    ]),
    rowToCsvLine([
      'tags',
      t.tags.join(';'),
      'Separate multiple tags with semicolons (;)',
    ]),
    rowToCsvLine([
      'information',
      '',
      'Optional additional info. Max 1000 characters. Leave empty if using FAQ below. Do not fill both FAQ and information.',
    ]),
    '',
    '# Data descriptors — one attribute per row',
    rowToCsvLine(['attributeName', 'parameterType', 'schemaType', 'description']),
  ];

  for (const d of t.dataDescriptors) {
    lines.push(
      rowToCsvLine([
        d.attributeName,
        d.parameterType,
        d.schemaType,
        d.description ?? '',
      ]),
    );
  }

  lines.push(...additionalInfoFaqCsvLines());

  return lines.join('\r\n');
}

function metadataRecordFromFields(
  meta: Record<string, string>,
  descriptors: Array<{
    attributeName: string;
    parameterType: string;
    schemaType: string;
    description?: string;
    unitCode?: string;
    unitText?: string;
  }>,
): Record<string, unknown> {
  const typeRaw = meta['type'] || DATASET_METADATA_TYPE;
  const tagsRaw = meta['tags'] ?? '';
  const tags = tagsRaw
    ? tagsRaw
        .split(/[;|]/)
        .map((t) => t.trim())
        .filter(Boolean)
    : [];
  const startYear = Number(meta['startYear']);
  const endYear = Number(meta['endYear']);

  return {
    type: [typeRaw.includes('adex:') ? typeRaw : DATASET_METADATA_TYPE],
    title: meta['title'] ?? '',
    permission: meta['permission'] || 'Open',
    shortDescription: meta['shortDescription'] ?? '',
    longDescription: meta['longDescription'] ?? '',
    license: meta['license'] ?? '',
    otherLicense: meta['otherLicense'] ?? '',
    uploadFrequency: meta['uploadFrequency'] ?? '',
    yearRange: {
      startYear: Number.isFinite(startYear) ? startYear : null,
      endYear: Number.isFinite(endYear) ? endYear : null,
    },
    geoCoverage: meta['geoCoverage'] ?? '',
    datasetType: meta['datasetType'] ?? '',
    fileFormat: meta['fileFormat'] ?? '',
    tags,
    dataDescriptors: descriptors,
  };
}

function parseVerticalDatasetMetadataCsv(
  rows: string[][],
): Record<string, unknown> {
  const meta: Record<string, string> = {};
  const descriptors: Array<{
    attributeName: string;
    parameterType: string;
    schemaType: string;
    description?: string;
    unitCode?: string;
    unitText?: string;
  }> = [];
  const faqRows: AdditionalInfoFaqRow[] = [];
  let section: 'meta' | 'desc' | 'faq' | null = null;

  for (const row of rows) {
    const c0 = (row[0] ?? '').trim();
    if (c0.startsWith('#')) {
      continue;
    }
    const c0Lower = c0.toLowerCase();
    if (c0Lower === 'field') {
      section = 'meta';
      continue;
    }
    if (c0Lower === 'attributename') {
      section = 'desc';
      continue;
    }
    if (c0Lower === 'question' && (row[1] ?? '').trim().toLowerCase() === 'answer' && !(row[2] ?? '').trim()) {
      section = 'faq';
      continue;
    }
    if (!c0 && section !== 'faq') {
      continue;
    }

    if (section === 'meta') {
      meta[c0] = (row[1] ?? '').trim();
      continue;
    }

    if (section === 'desc') {
      const attributeName = c0;
      const parameterType = (row[1] ?? '').trim();
      const schemaType = (row[2] ?? '').trim();
      const description = (row[3] ?? '').trim();
      if (attributeName && parameterType && schemaType) {
        const entry: {
          attributeName: string;
          parameterType: string;
          schemaType: string;
          description?: string;
        } = { attributeName, parameterType, schemaType };
        if (description) {
          entry.description = description;
        }
        descriptors.push(entry);
      }
      continue;
    }

    if (section === 'faq') {
      const question = c0;
      const answer = (row[1] ?? '').trim();
      if (question || answer) {
        faqRows.push({ question, answer });
      }
    }
  }

  if (Object.keys(meta).length === 0) {
    throw new Error(
      'Your CSV file is missing metadata. Add rows under the field and value columns using the downloaded template.',
    );
  }

  return {
    ...metadataRecordFromFields(meta, descriptors),
    additionalInfo: additionalInfoFromCsv(meta['information'] ?? '', faqRows),
  };
}

/** Legacy wide layout (rowKind + columns); still accepted on upload. */
function parseHorizontalDatasetMetadataCsv(
  rows: string[][],
): Record<string, unknown> {
  if (rows.length < 2) {
    throw new Error(
      'Your CSV file looks empty. Download the template, add your dataset details, and upload again.',
    );
  }

  const header = rows[0].map((h) => h.trim());
  const col = (name: string): number => header.indexOf(name);

  const idx = {
    rowKind: col('rowKind'),
    type: col('type'),
    title: col('title'),
    permission: col('permission'),
    shortDescription: col('shortDescription'),
    longDescription: col('longDescription'),
    license: col('license'),
    otherLicense: col('otherLicense'),
    uploadFrequency: col('uploadFrequency'),
    startYear: col('startYear'),
    endYear: col('endYear'),
    geoCoverage: col('geoCoverage'),
    datasetType: col('datasetType'),
    fileFormat: col('fileFormat'),
    tags: col('tags'),
    attributeName: col('attributeName'),
    parameterType: col('parameterType'),
    schemaType: col('schemaType'),
    description: col('description'),
  };

  const get = (row: string[], key: keyof typeof idx): string => {
    const i = idx[key];
    return i >= 0 ? (row[i] ?? '').trim() : '';
  };

  let metaRow: string[] | null = null;
  const descriptors: Array<{
    attributeName: string;
    parameterType: string;
    schemaType: string;
    description?: string;
    unitCode?: string;
    unitText?: string;
  }> = [];

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const kind =
      idx.rowKind >= 0
        ? get(row, 'rowKind').toLowerCase()
        : r === 1
          ? 'metadata'
          : 'descriptor';

    if (kind === 'descriptor') {
      const attributeName = get(row, 'attributeName');
      const parameterType = get(row, 'parameterType');
      const schemaType = get(row, 'schemaType');
      const description = get(row, 'description');
      if (attributeName && parameterType && schemaType) {
        const entry: {
          attributeName: string;
          parameterType: string;
          schemaType: string;
          description?: string;
        } = { attributeName, parameterType, schemaType };
        if (description) {
          entry.description = description;
        }
        descriptors.push(entry);
      }
      continue;
    }

    if (kind === 'metadata' || !metaRow) {
      metaRow = row;
    }
  }

  if (!metaRow) {
    throw new Error(
      'Your CSV file is missing a metadata row. Download the template and include one row marked as metadata.',
    );
  }

  const meta: Record<string, string> = {
    type: get(metaRow, 'type') || DATASET_METADATA_TYPE,
    title: get(metaRow, 'title'),
    permission: get(metaRow, 'permission'),
    shortDescription: get(metaRow, 'shortDescription'),
    longDescription: get(metaRow, 'longDescription'),
    license: get(metaRow, 'license'),
    otherLicense: get(metaRow, 'otherLicense'),
    uploadFrequency: get(metaRow, 'uploadFrequency'),
    startYear: get(metaRow, 'startYear'),
    endYear: get(metaRow, 'endYear'),
    geoCoverage: get(metaRow, 'geoCoverage'),
    datasetType: get(metaRow, 'datasetType'),
    fileFormat: get(metaRow, 'fileFormat'),
    tags: get(metaRow, 'tags'),
  };

  return metadataRecordFromFields(meta, descriptors);
}

/** Convert filled CSV template to the JSON shape used by validation / onboarding. */
export function parseDatasetMetadataCsv(
  csvText: string,
): Record<string, unknown> {
  const rows = parseCsvRows(csvText).filter((row) => {
    const first = (row[0] ?? '').trim();
    return first && !first.startsWith('#');
  });
  if (rows.length < 2) {
    throw new Error(
      'Your CSV file looks empty. Download the template, add your dataset details, and upload again.',
    );
  }

  const firstHeader = (rows[0][0] ?? '').trim().toLowerCase();
  if (firstHeader === 'field') {
    return parseVerticalDatasetMetadataCsv(rows);
  }
  if (firstHeader === 'rowkind') {
    return parseHorizontalDatasetMetadataCsv(rows);
  }

  throw new Error(
    'We could not read this CSV file. Download the template and use the field and value columns.',
  );
}

function downloadTextFile(
  content: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function downloadDatasetMetadataTemplate(
  filename = 'dataset-metadata-template.json',
): void {
  const json = JSON.stringify(buildDatasetMetadataTemplate(), null, 2);
  downloadTextFile(json, filename, 'application/json');
}

export function downloadDatasetMetadataTemplateCsv(
  filename = 'dataset-metadata-template.csv',
): void {
  downloadTextFile(
    buildDatasetMetadataTemplateCsv(),
    filename,
    'text/csv;charset=utf-8',
  );
}

export interface MetadataJsonValidationResult {
  valid: boolean;
  typeError: string | null;
  missingFields: string[];
}

export interface MetadataValidationOptions {
  /** When true, enforces datasetType === 'semi Structured' and fileFormat === 'gpkg'. */
  requireOgcFeature?: boolean;
}

export function validateDatasetMetadataJson(
  json: unknown,
  options?: MetadataValidationOptions,
): MetadataJsonValidationResult {
  const missing: string[] = [];
  let typeError: string | null = null;

  if (!json || typeof json !== 'object') {
    return {
      valid: false,
      typeError:
        'This file could not be read. Download the template, fill it in, and upload a valid JSON or CSV file.',
      missingFields: [],
    };
  }

  const data = stripTemplateGuideKeys(json as Record<string, unknown>);
  const uploadedType = data['type'];

  if (!uploadedType || !Array.isArray(uploadedType)) {
    typeError =
      'Your file is missing the dataset type. Download the template and keep the type field unchanged.';
  } else if (!uploadedType.includes(DATASET_METADATA_TYPE)) {
    typeError =
      'The dataset type in your file is not supported. Download the template and do not change the type field.';
  }

  const title = (data['title'] ?? data['name'] ?? '') as string;
  if (!String(title).trim()) {
    missing.push('Dataset title');
  }

  const permission = data['permission'];
  if (
    permission !== 'Open' &&
    permission !== 'Restricted' &&
    permission !== 'Private' &&
    permission !== 'OPEN' &&
    permission !== 'RESTRICTED' &&
    permission !== 'PRIVATE'
  ) {
    missing.push('Access permission (choose Open, Restricted, or Private)');
  }

  const stringRequired: Array<{ key: string; label: string }> = [
    { key: 'shortDescription', label: 'Short description' },
    { key: 'license', label: 'License' },
    { key: 'uploadFrequency', label: 'Upload frequency' },
    { key: 'geoCoverage', label: 'Geographic coverage' },
    { key: 'datasetType', label: 'Dataset type' },
    { key: 'fileFormat', label: 'File format' },
  ];

  for (const { key, label } of stringRequired) {
    const value = data[key];
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      missing.push(label);
    }
  }

  const license = String(data['license'] ?? '').trim();
  if (license === 'Other') {
    const other = String(data['otherLicense'] ?? '').trim();
    if (!other) {
      missing.push('Other license name (required when license is Other)');
    }
  }

  const longDesc = data['longDescription'] ?? data['description'];
  if (
    longDesc === undefined ||
    longDesc === null ||
    (typeof longDesc === 'string' && longDesc.trim() === '')
  ) {
    missing.push('Long description');
  }

  const tags = data['tags'];
  if (!Array.isArray(tags) || tags.length === 0) {
    missing.push('At least one tag');
  } else {
    const tooLong = (tags as unknown[])
      .map((t) => String(t).trim())
      .filter((t) => t.length > 20);
    if (tooLong.length > 0) {
      missing.push(`Each tag must be 20 characters or fewer (too long: ${tooLong.join(', ')})`);
    }
  }

  const yearRange = data['yearRange'] as Record<string, unknown> | undefined;
  if (
    !yearRange ||
    yearRange['startYear'] == null ||
    yearRange['endYear'] == null
  ) {
    missing.push('Data year range (start year and end year)');
  } else {
    const start = Number(yearRange['startYear']);
    const end = Number(yearRange['endYear']);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      missing.push('Start year and end year (use 4-digit years, for example 2020 and 2024)');
    } else if (start > end) {
      missing.push('End year must be the same as or after the start year');
    }
  }

  const datasetType = String(data['datasetType'] ?? '').trim();
  if (
    datasetType &&
    !DATASET_TYPE_VALUES.includes(datasetType as (typeof DATASET_TYPE_VALUES)[number])
  ) {
    missing.push(
      `Dataset type (choose one of: ${DATASET_TYPE_VALUES.join(', ')})`,
    );
  }

  const fileFormat = String(data['fileFormat'] ?? '').trim();

  if (options?.requireOgcFeature) {
    if (datasetType && datasetType !== OGC_FEATURE_DATASET_TYPE) {
      missing.push(
        `Dataset type must be "${OGC_FEATURE_DATASET_TYPE}" for OGC Feature datasets`,
      );
    }
    if (fileFormat && fileFormat.toLowerCase() !== OGC_FEATURE_FILE_FORMAT) {
      missing.push(
        `File format must be "${OGC_FEATURE_FILE_FORMAT}" (GeoPackage) for OGC Feature datasets`,
      );
    }
  } else if (datasetType && fileFormat) {
    const allowed =
      FILE_FORMAT_BY_DATASET_TYPE[datasetType]?.map((f) => f.toLowerCase()) ??
      [];
    if (
      allowed.length > 0 &&
      !allowed.includes(fileFormat.toLowerCase())
    ) {
      missing.push(
        `File format for "${datasetType}" datasets (allowed: ${FILE_FORMAT_BY_DATASET_TYPE[datasetType]?.join(', ')})`,
      );
    }
  }

  if (!typeError) {
    typeError = additionalInfoMutualExclusionError(data);
  }

  return {
    valid: !typeError && missing.length === 0,
    typeError,
    missingFields: missing,
  };
}

export function parseDatasetMetadataToOnboarding(
  json: unknown,
): NgsildOnboardingPayload {
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid metadata JSON');
  }
  const data = stripTemplateGuideKeys(json as Record<string, unknown>);

  const rawPermission = String(data['permission'] ?? 'Open');
  const permissionUpper = rawPermission.toUpperCase();
  const permission: NgsildBasicInfo['permission'] =
    rawPermission === 'Restricted' || permissionUpper === 'RESTRICTED'
      ? 'Restricted'
      : rawPermission === 'Private' ||
          rawPermission === 'Privacy Computation' ||
          permissionUpper === 'PRIVATE'
        ? 'Private'
        : 'Open';

  const yearRangeRaw = data['yearRange'] as Record<string, unknown> | undefined;
  const yearRange: NgsildYearRange | null = yearRangeRaw
    ? {
        startYear: Number(yearRangeRaw['startYear']) || null,
        endYear: Number(yearRangeRaw['endYear']) || null,
      }
    : null;

  const tags = Array.isArray(data['tags'])
    ? data['tags'].map((t) => String(t).trim()).filter(Boolean)
    : [];

  return {
    basic: {
      title: String(data['title'] ?? data['name'] ?? '').trim(),
      permission,
      shortDescription: String(data['shortDescription'] ?? '').trim(),
      longDescription: String(
        data['longDescription'] ?? data['description'] ?? '',
      ).trim(),
    },
    metadata: {
      tags,
      yearRange,
      geoCoverage: String(data['geoCoverage'] ?? '').trim(),
      uploadFrequency: String(data['uploadFrequency'] ?? '').trim(),
      license: String(data['license'] ?? '').trim(),
      otherLicense: String(data['otherLicense'] ?? '').trim(),
      datasetType: String(data['datasetType'] ?? '').trim(),
      fileFormat: matchFileFormatToOptionValue(
        String(data['fileFormat'] ?? '').trim(),
        FILE_FORMAT_BY_DATASET_TYPE[String(data['datasetType'] ?? '').trim()] ?? [],
      ),
      dataDescriptors: importDataDescriptorsFromJson(data['dataDescriptors']),
      dataSample: data['dataSample'] ?? data['data_sample'] ?? null,
    },
  };
}
